import CommitApi from '../../api/commitApi';
import React, { useCallback, useEffect, useState } from 'react';
import { ConfigFileModel } from '../../models/configFileModel';
import { DiffEntry, DiffEntryStatus, DiffModel } from '../../models/diffModel';
import { AppErrorList, useAppErrorList } from '../components/AppErrorList';
import Grid from '@mui/material/Grid';
import CircularProgress from '@mui/material/CircularProgress';
import SideBySideDiffView from '../components/DiffView/SideBySideDiffView';
import Typography from '@mui/material/Typography';

type Props = {
  readonly baseCommitId?: string;
  readonly commitId?: string;
  readonly showAffectedFilesInfo?: boolean;
};

const NO_CHANGES_DIFF: DiffModel = { diffs: [] };

const isModifiedDiff = (diffEntry: DiffEntry) => diffEntry.status !== DiffEntryStatus.UNMODIFIED

const markFileAsAdded = (configFile: ConfigFileModel): DiffEntry => ({
  resourceName: configFile.filename,
  status: DiffEntryStatus.ADDED,
  next: {
    configFile: {
      id: configFile.id,
      filename: configFile.filename,
      content: configFile.content,
      size: configFile.size,
    },
  },
});

const markFileAsRemoved = (configFile: ConfigFileModel): DiffEntry => ({
  resourceName: configFile.filename,
  status: DiffEntryStatus.REMOVED,
  prev: {
    configFile: {
      id: configFile.id,
      filename: configFile.filename,
      content: configFile.content,
      size: configFile.size,
    },
  },
});

export const CommitDiffContainer = (props: Props) => {
  const { baseCommitId, commitId, showAffectedFilesInfo = false } = props;

  const [diff, setDiff] = useState<DiffModel>();
  const [isLoading, setLoading] = useState<boolean>();
  const [{ errors, hasErrors }, { addError }] = useAppErrorList();

  const diffBasedOnCommits = useCallback((baseCommitId: string, commitId: string) => {
    setLoading(true);

    CommitApi.fetchCommitDiff(baseCommitId, commitId)
      .then((diffModel: DiffModel) => {
        setDiff({ diffs: diffModel.diffs.filter(isModifiedDiff) } );
      })
      .catch(addError)
      .finally(() => setLoading(false));
  }, [addError]);

  const diffBasedOnSingleCommit = useCallback((commitId: string, isBaseCommit: boolean) => {
    setLoading(true);

    CommitApi.fetchConfigFilesByCommitId(commitId)
      .then((configFiles: ConfigFileModel[]) => {
        const mapFunc = isBaseCommit ? markFileAsRemoved : markFileAsAdded;

        setDiff({ diffs: configFiles.map(mapFunc) });
      })
      .catch(addError)
      .finally(() => setLoading(false));
  }, [addError]);

  useEffect(() => {
    if (baseCommitId && commitId) {
      diffBasedOnCommits(baseCommitId, commitId);
    } else if (!baseCommitId && commitId) {
      diffBasedOnSingleCommit(commitId, false);
    } else if (baseCommitId && !commitId) {
      diffBasedOnSingleCommit(baseCommitId, true);
    } else {
      setDiff(NO_CHANGES_DIFF);
    }
  },[diffBasedOnCommits, diffBasedOnSingleCommit, baseCommitId, commitId]);

  const affectedFileCount = diff?.diffs.length;

  return (
    <Grid item xs={12}>
      { showAffectedFilesInfo && (
        <Typography style={{ margin: '20px 0' }}>{affectedFileCount} file(s) will be affected</Typography>
      )}

      { hasErrors && (
        <Grid item xs={12}>
          <AppErrorList errors={errors} />
        </Grid>
      )}

      <Grid item xs={12} display="flex" justifyContent="center">
        { isLoading ? (
          <CircularProgress size={20} />
        ) : (
          <SideBySideDiffView diffModel={diff} />
        )}
      </Grid>
    </Grid>
  );
}

export default CommitDiffContainer;
